import React, { useState, useEffect, useRef } from 'react';
import { APP_S3, MANIPULATE_DESCRIPTION } from '../../../utils';
import axios from 'axios';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';

import JsZip from 'jszip';
import FileSaver from 'file-saver';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useSelector } from 'react-redux';
import { selectedClientSelector } from '../../../store/slices/selectedClient';

const UserGallery = ({
  userId,
  clientName,
  layoutCols = 3,
  setAppImage,
  checkImg,
  maxHeight = '1505px'
}) => {
  const urlInput = useRef();
  const [showImgModal, setShowImgModal] = useState([null, null]);
  const [isCopied, setIsCopied] = useState(false);
  const [totalProgress, setTotalProgress] = useState(0);
  const [images, setImages] = useState([]);
  const [allDescriptions, setAllDescriptions] = useState(null);
  const [allUrls, setAllUrls] = useState([]);
  const [current, setCurrent] = useState(0);
  const [isDownloading, setIsDownLoading] = useState(false);
  const [createdTime, setCreatedTime] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const { selectedClient } = useSelector(selectedClientSelector);

  useEffect(() => {
    setImages([]);
    setAllDescriptions(null);
    setCreatedTime(null);
    setSelectedImage(null);
  }, [userId]);

  // No longer needed, using Tailwind for layout

  const download = async (url) => {
    try {
      const resp = await fetch(url);

      return await resp.blob();
    } catch (err) {
      console.log(err);
    }
  };

  const downloadByGroup = async (urls) => {
    let counter = 0;
    try {
      const downloadPromises = urls.map(async (url) => {
        try {
          return download(url);
        } catch (error) {
          console.error(`Error downloading ${url}:`, error);
        }
      });
      downloadPromises.map((promise) => {
        return promise.then(() => {
          counter++;
          setTotalProgress(
            Math.floor((counter * 100) / downloadPromises.length)
          );
        });
      });
      return Promise.all(downloadPromises);
    } catch (error) {
      console.error('Error during download:', error);
      throw error;
    }
  };

  const exportZip = (blobs) => {
    const zip = JsZip();
    blobs.forEach(async (blob, i) => {
      let mime = blob?.type;
      if (mime === 'image/jpeg') {
        mime = 'jpg';
      } else if (mime === 'image/png') {
        mime = 'png';
      } else {
        mime = 'jpg';
      }
      zip.file(`file-${i}.${mime}`, blob);
    });

    zip.generateAsync({ type: 'blob' }).then((zipFile) => {
      setTimeout(() => {
        const fileName = `${selectedClient.clientName}-images.zip`;
        setIsDownLoading(false);
        setTotalProgress(0);

        return FileSaver.saveAs(zipFile, fileName);
      }, 1000);
    });
  };

  const downloadAndZip = async (urls) => {
    const blobs = await downloadByGroup(urls);
    return exportZip(blobs);
  };

  const copyUrl = () => {
    urlInput.select();
    document.execCommand('copy');
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 4500);
  };

  useEffect(() => {
    const handleLoadImages = async (imgs, allDescriptions) => {
      // Reverse for newest first
      const reversed = imgs.reverse();
      setAllUrls(
        reversed.map((item) => ({
          url: item.url,
          LastModified: item.LastModified
        }))
      );
      setImages(reversed);
    };

    const getImages = async () => {
      if (userId) {
        await axios
          .get(`${APP_S3}?userId=${userId}`)
          .then(async (response) => {
            if (response.data.body.length) {
              let z = response.data.body;
              let config = {
                method: 'post',
                url: MANIPULATE_DESCRIPTION,
                data: {
                  userid: userId,
                  get: true,
                  to_suite: true
                }
              };
              await axios(config).then((res) => {
                if (res.data?.images) {
                  setAllDescriptions(res.data.images);
                  handleLoadImages(z, res.data.images);
                } else {
                  setAllDescriptions(null);
                  handleLoadImages(z, null);
                }
              });
            }
          })
          .catch((err) => {
            console.error(err);
          });
      }
    };

    getImages();
  }, [userId, layoutCols]);

  const handleClose = () => {
    setShowImgModal([null, null]);
    setIsCopied(false);
  };

  return (
    <>
      <div
        className="grid grid-cols-3 gap-3 overflow-y-auto"
        style={{ maxHeight }}>
        {images.length ? (
          images.map((data, idx) => {
            let url = data.url;
            let urlOriginal = url.replace('/thumbnails', '');
            const isUsed = allDescriptions && allDescriptions[url]?.isUsed;
            const isSelected = selectedImage === urlOriginal || current === idx;
            return (
              <div
                key={idx}
                className={`relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all aspect-square ${
                  isSelected
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-transparent hover:border-muted-foreground/30'
                }`}
                style={{ background: '#f8fafc' }}
                onClick={() => {
                  setSelectedImage(urlOriginal);
                  setCurrent(idx);
                  const dateFormat = data?.LastModified
                    ? new Intl.DateTimeFormat('en-US', {
                        dateStyle: 'medium',
                        timeStyle: 'long'
                      }).format(new Date(data.LastModified))
                    : '';
                  setCreatedTime(dateFormat);
                  let ev = { target: { value: urlOriginal } };
                  if (!clientName) {
                    setAppImage(ev);
                    let justUrls = images.map((item) => item.url);
                    let des = '';
                    allDescriptions !== null
                      ? typeof allDescriptions?.url === 'object'
                        ? allDescriptions[url]?.description
                          ? (des = allDescriptions[url]?.description)
                          : (des = '')
                        : allDescriptions[url]
                        ? (des = allDescriptions[url])
                        : (des = '')
                      : (des = '');
                    checkImg([justUrls, ev.target.value, des]);
                  }
                }}>
                <img
                  src={url}
                  alt={`Gallery ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
                {isUsed && (
                  <div className="absolute bottom-1.5 left-1.5 bg-muted-foreground/80 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-md">
                    Used
                  </div>
                )}
                {isSelected && (
                  <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                    <Check className="h-6 w-6 text-primary-foreground bg-primary rounded-full p-1" />
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="min-h-[200px] flex items-center justify-center text-muted-foreground col-span-3">
            <p className="text-base">No photos</p>
          </div>
        )}
      </div>
      <div className="border border-border rounded-xl p-4 space-y-2 mt-4">
        <label className="text-base font-semibold">
          App Gallery Description
        </label>
        <p
          className={`text-sm ${
            allDescriptions &&
            (typeof allDescriptions[images[current]?.url] === 'object'
              ? allDescriptions[images[current]?.url]?.description
              : allDescriptions[images[current]?.url])
              ? ''
              : 'text-destructive'
          }`}>
          {allDescriptions
            ? typeof allDescriptions[images[current]?.url] === 'object'
              ? allDescriptions[images[current]?.url]?.description
                ? allDescriptions[images[current]?.url]?.description
                : 'No description'
              : allDescriptions[images[current]?.url]
              ? allDescriptions[images[current]?.url]
              : 'No description'
            : 'No description'}
        </p>
      </div>
    </>
  );
};

export default UserGallery;
